import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  StarIcon as StarOutline,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolid, EyeIcon } from "@heroicons/react/24/solid";
import { Menu } from "@headlessui/react";
import ModalRenderer from "../../components/modal/ModalRenderer";
import { adminApi } from "../../repositories/admin";
import Swal from "sweetalert2";
import Pagination from "../../components/common/Pagination";
import {
  rowMenuPanelClass,
  rowMenuTransitionClass,
  rowMenuTriggerClass,
} from "../../components/ui/RowActionsMenu";
import PageHeader from "../../components/ui/PageHeader";
import Button from "../../components/ui/Button";
import StatusBadge from "../../components/ui/StatusBadge";
import { SkeletonTableRows } from "../../components/ui/skeleton";
import DataTable, { DataTableColumn, dataTableRowClass, dataTableTdClass } from "../../components/ui/DataTable";
import { cn } from "../../DashboardPage/lib/utils";

// Predefined genre options
const MAIN_GENRES = [
  "Acoustic",
  "Children's",
  "Classical",
  "Country",
  "Dance & Electronic",
  "Holiday",
  "Indian Music",
  "Indie & Alternative",
  "Jazz",
  "Latin Music",
  "New Age",
  "Non-Music",
  "Pop",
  "R&B",
  "Rap & Hip-Hop",
  "Religious Music",
  "Rock",
  "World",
];

// Predefined platform options
const PLATFORMS = [
  "spotify",
  "apple",
  "amazon",
  "deezer",
  "tidal",
  "audiomack",
  "soundcloud",
  "youtube",
  "siriusxm",
];

interface Pitch {
  id: number;
  userId: number;
  artistId: number | null;
  artistName: string;
  releaseTitle: string;
  releaseDate: string;
  configuration: string;
  releaseUPC: string;
  focusTrackName: string;
  trackISRC: string;
  featuredArtists: string;
  featuredArtistURI: string;
  altTitles: string;
  shortDescription: string;
  longDescription: string;
  lyrics: string;
  songDescription: string;
  mainGenre: string;
  songMood: string;
  language: string;
  linkToListen: string;
  promoPhotos: string;
  images: any[] | null;
  status: string;
  companyName: string;
  createdAt: string;
  updatedAt: string;
  AmazonInfo: any;
  SpotifyInfo: any;
  DeezerInfo: any;
  TidalInfo: any;
  AudiomackInfo: any;
  SoundCloudInfo: any;
  AppleInfo: any;
  YoutubeInfo: any;
  SiriusXMInfo: any;
  contributors: any[];
  starred: boolean;
}

type SortColumn = "companyName" | "artistName" | "releaseTitle" | "releaseDate" | "mainGenre" | "configuration";
type SortDirection = "asc" | "desc";

const DspDashboard: React.FC = () => {
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchType, setSearchType] = useState<"artist" | "release">("artist");
  const searchTypeRef = React.useRef<"artist" | "release">("artist");
  const [showStarredOnly, setShowStarredOnly] = useState(false);
  const [filterExpanded, setFilterExpanded] = useState(false);

  // Keep ref in sync with state
  React.useEffect(() => {
    searchTypeRef.current = searchType;
  }, [searchType]);

  // Applied filter states (used for API calls)
  const [companyFilter, setCompanyFilter] = useState<number[]>([]); // Changed to number[] for IDs
  const [genreFilter, setGenreFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [platformFilter, setPlatformFilter] = useState<string[]>([]);

  // Temporary filter states (used for UI selections before applying)
  const [tempCompanyFilter, setTempCompanyFilter] = useState<number[]>([]); // Changed to number[] for IDs
  const [tempGenreFilter, setTempGenreFilter] = useState<string[]>([]);
  const [tempStatusFilter, setTempStatusFilter] = useState<string[]>([]);
  const [tempPlatformFilter, setTempPlatformFilter] = useState<string[]>([]);

  // Sort states
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // Modal state
  const [selectedPitch, setSelectedPitch] = useState<Pitch | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  // Pagination from API
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Labels/Companies from API
  const [labels, setLabels] = useState<{ id: number; name: string }[]>([]);

  // Get unique values for filters - now returns label objects with IDs
  const uniqueCompanies = useMemo(() => {
    // Count occurrences of each name
    const nameCount = labels.reduce((acc, label) => {
      acc[label.name] = (acc[label.name] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Return labels with display names that include ID if name is duplicated
    return labels
      .map(label => ({
        id: label.id,
        name: label.name,
        displayName: nameCount[label.name] > 1 ? `${label.name} (ID: ${label.id})` : label.name
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [labels]);

  // Use predefined genre list
  const uniqueGenres = MAIN_GENRES;

  const fetchPitches = useCallback(async (page: number) => {
    try {
      setLoading(true);

      // Build API query parameters
      const params: any = {
        page,
        limit: itemsPerPage,
      };

      // Only add search params if there's a search term
      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
        params.searchType = searchTypeRef.current;
      }

      // Add filters
      if (genreFilter.length > 0) {
        params.mainGenre = genreFilter.join(", ");
      }
      if (companyFilter.length > 0) {
        // Convert label IDs to names for the API
        const companyNames = companyFilter
          .map(id => labels.find(l => l.id === id)?.name)
          .filter(Boolean);
        params.companyName = companyNames.join(", ");
      }
      if (statusFilter.length > 0) {
        params.status = statusFilter.join(", ");
      }
      if (platformFilter.length > 0) {
        params.platform = platformFilter.join(", ");
      }

      // Call different API based on starred toggle
      const response = showStarredOnly
        ? await adminApi.getStarredDspPitches(params)
        : await adminApi.getDspPitches(params);

      if (response?.data?.success) {
        let fetchedPitches = response.data.data || [];

        setPitches(fetchedPitches);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setTotalItems(response.data.pagination?.totalItems || 0);
      }
    } catch (error: any) {
      console.error("Error fetching pitches:", error);
      Swal.fire({
        title: "Error!",
        text: error?.response?.data?.message || "Failed to fetch pitches",
        icon: "error",
      });
      setPitches([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm, genreFilter, companyFilter, statusFilter, platformFilter, showStarredOnly, labels]);

  const fetchLabels = useCallback(async () => {
    try {
      const response = await adminApi.getLabels();
      if (response?.data?.success) {
        setLabels(response.data.data || []);
      }
    } catch (error: any) {
      console.error("Error fetching labels:", error);
    }
  }, []);

  // Fetch labels on mount
  useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 and fetch when filters change
  useEffect(() => {
    setCurrentPage(1);
    fetchPitches(1);
  }, [debouncedSearchTerm, genreFilter, companyFilter, statusFilter, platformFilter, showStarredOnly, fetchPitches]);

  const handleStarToggle = async (pitchId: number) => {
    try {
      const pitch = pitches.find(p => p.id === pitchId);
      const wasStarred = pitch?.starred || false;

      await adminApi.toggleStarredPitch(pitchId);

      // If unstarring (was starred, now unstarred) and we're viewing starred only
      if (wasStarred && showStarredOnly) {
        // Refresh the starred pitches list to remove the unstarred item
        fetchPitches(currentPage);
      } else {
        // Update local state
        setPitches(pitches.map(p =>
          p.id === pitchId ? { ...p, starred: !p.starred } : p
        ));
      }
    } catch (error: any) {
      console.error("Error toggling star:", error);
      Swal.fire({
        title: "Error!",
        text: error?.response?.data?.message || "Failed to toggle star",
        icon: "error",
      });
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleViewPitch = (pitch: Pitch) => {
    setSelectedPitch(pitch);
    setIsViewModalOpen(true);
  };

  // Sorted data (client-side sorting)
  const sortedPitches = useMemo(() => {
    if (!sortColumn) return pitches;

    const sorted = [...pitches].sort((a, b) => {
      let aValue: string | number = a[sortColumn];
      let bValue: string | number = b[sortColumn];

      if (sortColumn === "releaseDate") {
        aValue = new Date(aValue as string).getTime();
        bValue = new Date(bValue as string).getTime();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [pitches, sortColumn, sortDirection]);

  const handleFilterChange = (
    filterType: "company" | "genre" | "status" | "platform",
    value: string | number
  ) => {
    if (filterType === "company") {
      // Handle company filter with IDs
      const id = value as number;
      setTempCompanyFilter((prev: number[]) =>
        prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
      );
    } else {
      // Handle other filters with strings
      const setFilter = {
        genre: setTempGenreFilter,
        status: setTempStatusFilter,
        platform: setTempPlatformFilter,
      }[filterType];

      setFilter((prev: string[]) =>
        prev.includes(value as string) ? prev.filter(v => v !== value) : [...prev, value as string]
      );
    }
  };

  const applyFilters = () => {
    setCompanyFilter(tempCompanyFilter);
    setGenreFilter(tempGenreFilter);
    setStatusFilter(tempStatusFilter);
    setPlatformFilter(tempPlatformFilter);
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setCompanyFilter([]);
    setGenreFilter([]);
    setStatusFilter([]);
    setPlatformFilter([]);
    setTempCompanyFilter([]);
    setTempGenreFilter([]);
    setTempStatusFilter([]);
    setTempPlatformFilter([]);
    setShowStarredOnly(false);
  };

  const hasActiveFilters = debouncedSearchTerm || companyFilter.length > 0 || genreFilter.length > 0 ||
    statusFilter.length > 0 || platformFilter.length > 0 || showStarredOnly;

  const SortIcon = ({ column }: { column: SortColumn }) => {
    if (sortColumn !== column) return null;
    return sortDirection === "asc" ? (
      <ChevronUpIcon className="inline w-4 h-4 ml-1" />
    ) : (
      <ChevronDownIcon className="inline w-4 h-4 ml-1" />
    );
  };

  // Header markup is supplied via DataTable's `head` prop (the columns are
  // sortable); this list only sets the column count for its state rows.
  const dspPitchColumns: DataTableColumn<any>[] = [
    { key: "star", header: "Star" },
    { key: "companyName", header: "Label" },
    { key: "artistName", header: "Artist" },
    { key: "releaseTitle", header: "Release" },
    { key: "releaseDate", header: "Release Date" },
    { key: "mainGenre", header: "Genre" },
    { key: "configuration", header: "Type" },
    { key: "status", header: "Status" },
    { key: "pitch", header: "Pitch" },
    { key: "action", header: "Action" },
  ];
  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchPitches(newPage);
    }
  };

  // Parse genres for display (handle comma-separated values)
  const getGenreDisplay = (mainGenre: string) => {
    if (!mainGenre) return "N/A";
    const genres = mainGenre.split(',').map(g => g.trim());
    return genres.join(", ");
  };

  // No full-page loader: the table below already renders skeleton rows while
  // `loading`, and returning early meant that path never ran on first load —
  // the whole page (header and search included) blanked instead.

  return (
    <div className="py-2 sm:p-6">
      {/* Header */}
      <PageHeader
        breadcrumbs={[{ label: "Dashboard" }]}
        className="mb-6"
        title="DSP Dashboard"
        description="Pitches submitted by the labels in your organization."
      />

      {/* Pitches Section Header with Search */}
      <div className="flex flex-col gap-4 px-4 py-3 bg-white border rounded-t-lg border-neutral-200 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-base font-semibold text-black sm:text-lg">Pitches</h3>

        {/* Search */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex items-stretch w-full bg-white border rounded-lg border-neutral-300 focus-within:border-primary-500 sm:w-auto">
            <div className="relative shrink-0">
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value as "artist" | "release")}
                aria-label="Search pitches by field"
                className="w-[104px] sm:w-auto h-full py-2 pl-3 text-sm truncate bg-transparent border-0 appearance-none cursor-pointer pr-7 text-neutral-600 focus:outline-none focus:ring-0"
              >
                <option value="artist">Artist Name</option>
                <option value="release">Release Name</option>
              </select>
              <ChevronDownIcon className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-2 top-1/2 text-neutral-400" />
            </div>

            <span aria-hidden="true" className="self-stretch w-px my-1.5 bg-neutral-200" />

            <div className="relative flex-1 min-w-0 sm:w-64">
              <MagnifyingGlassIcon className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-neutral-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={`Search by ${searchType === "artist" ? "artist name" : "release name"}...`}
                className="w-full py-2 text-sm text-black bg-transparent border-0 rounded-r-lg pl-9 pr-9 placeholder-neutral-400 focus:outline-none focus:ring-0"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                  className="absolute p-0.5 -translate-y-1/2 rounded right-2 top-1/2 text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Clear All Filters */}
          {(companyFilter.length > 0 || genreFilter.length > 0 || statusFilter.length > 0 || platformFilter.length > 0 || showStarredOnly) && (
            <Button variant="secondary" onClick={clearAllFilters}>
              Clear All Filters
            </Button>
          )}
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white border-x border-neutral-200">
        <button
          onClick={() => setFilterExpanded(!filterExpanded)}
          className="flex items-center justify-between w-full p-3 text-left transition-colors border border-transparent hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700"
        >
          <div className="flex items-center">
            <FunnelIcon className="w-4 h-4 mr-2 text-neutral-500" />
            <span className="text-sm font-medium text-neutral-700">Filters</span>
            {(companyFilter.length + genreFilter.length + statusFilter.length + platformFilter.length) > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 ml-2 text-xs font-medium border rounded-full text-primary-800 bg-primary-50 border-primary-200">
                {companyFilter.length + genreFilter.length + statusFilter.length + platformFilter.length}
              </span>
            )}
          </div>
          {filterExpanded ? (
            <ChevronUpIcon className="w-4 h-4 text-neutral-500" />
          ) : (
            <ChevronDownIcon className="w-4 h-4 text-neutral-500" />
          )}
        </button>

        {filterExpanded && (
          <div className="p-3 border-t border-neutral-200 bg-neutral-50">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {/* Company Filter */}
              <div>
                <h4 className="mb-2 text-sm font-semibold text-neutral-700">Company</h4>
                <div className="flex flex-col gap-2 pr-1 overflow-y-auto max-h-40 scrollbar-thin">
                  {uniqueCompanies.length > 0 ? (
                    uniqueCompanies.map(company => (
                      <label key={company.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={tempCompanyFilter.includes(company.id)}
                          onChange={() => handleFilterChange("company", company.id)}
                          className="mr-2 w-4 h-4 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700"
                        />
                        <span className="text-sm text-neutral-700">{company.displayName}</span>
                      </label>
                    ))
                  ) : (
                    <span className="text-sm text-neutral-500">No companies available</span>
                  )}
                </div>
              </div>

              {/* Genre Filter */}
              <div>
                <h4 className="mb-2 text-sm font-semibold text-neutral-700">Genre</h4>
                <div className="flex flex-col gap-2 pr-1 overflow-y-auto max-h-40 scrollbar-thin">
                  {uniqueGenres.length > 0 ? (
                    uniqueGenres.map(genre => (
                      <label key={genre} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={tempGenreFilter.includes(genre)}
                          onChange={() => handleFilterChange("genre", genre)}
                          className="mr-2 w-4 h-4 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700"
                        />
                        <span className="text-sm text-neutral-700">{genre}</span>
                      </label>
                    ))
                  ) : (
                    <span className="text-sm text-neutral-500">No genres available</span>
                  )}
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <h4 className="mb-2 text-sm font-semibold text-neutral-700">Status</h4>
                <div className="flex flex-col gap-2">
                  {["submitted", "delivered"].map(status => (
                    <label key={status} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={tempStatusFilter.includes(status)}
                        onChange={() => handleFilterChange("status", status)}
                        className="mr-2 w-4 h-4 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700"
                      />
                      <span className="text-sm text-neutral-700 capitalize">{status}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Platform Filter */}
              <div>
                <h4 className="mb-2 text-sm font-semibold text-neutral-700">Platform</h4>
                <div className="flex flex-col gap-2 pr-1 overflow-y-auto max-h-40 scrollbar-thin">
                  {PLATFORMS.map(platform => (
                    <label key={platform} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={tempPlatformFilter.includes(platform)}
                        onChange={() => handleFilterChange("platform", platform)}
                        className="mr-2 w-4 h-4 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700"
                      />
                      <span className="text-sm text-neutral-700 capitalize">{platform}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Apply Filters Button */}
            <div className="flex justify-end gap-3 pt-3 mt-4 border-t border-neutral-200">
              <Button onClick={applyFilters}>
                Apply Filters
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Show Starred Only Toggle */}
      <div className="flex items-center justify-between p-3 bg-white border-b border-x border-neutral-200">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={showStarredOnly}
            onChange={(e) => {
              const isChecked = e.target.checked;
              // If unchecking (turning off starred filter), clear all filters
              if (!isChecked) {
                setSearchTerm("");
                setCompanyFilter([]);
                setGenreFilter([]);
                setStatusFilter([]);
                setPlatformFilter([]);
                setTempCompanyFilter([]);
                setTempGenreFilter([]);
                setTempStatusFilter([]);
                setTempPlatformFilter([]);
              }
              setShowStarredOnly(isChecked);
            }}
            className="mr-2 w-4 h-4 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700"
          />
          <StarSolid className="w-4 h-4 mr-1 text-warning-500" />
          <span className="text-sm font-medium text-neutral-700">Show starred releases only</span>
        </label>
      </div>

      {/* Table */}
      <DataTable
        columns={dspPitchColumns}
        className="rounded-t-none border-t-0"
        head={
                <thead>
                  <tr className="text-left border-b border-neutral-200 bg-primary-50">
                    <th className="w-12 px-4 py-3 text-xs font-semibold tracking-wide uppercase text-primary-900">Star</th>
                    <th className="px-4 py-3 text-xs font-semibold tracking-wide uppercase transition-colors cursor-pointer text-primary-900 hover:bg-primary-100" onClick={() => handleSort("companyName")}>
                      Label <SortIcon column="companyName" />
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold tracking-wide uppercase transition-colors cursor-pointer text-primary-900 hover:bg-primary-100" onClick={() => handleSort("artistName")}>
                      Artist <SortIcon column="artistName" />
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold tracking-wide uppercase transition-colors cursor-pointer text-primary-900 hover:bg-primary-100" onClick={() => handleSort("releaseTitle")}>
                      Release <SortIcon column="releaseTitle" />
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold tracking-wide uppercase transition-colors cursor-pointer text-primary-900 hover:bg-primary-100" onClick={() => handleSort("releaseDate")}>
                      Release Date <SortIcon column="releaseDate" />
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold tracking-wide uppercase transition-colors cursor-pointer text-primary-900 hover:bg-primary-100" onClick={() => handleSort("mainGenre")}>
                      Genre <SortIcon column="mainGenre" />
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold tracking-wide uppercase transition-colors cursor-pointer text-primary-900 hover:bg-primary-100" onClick={() => handleSort("configuration")}>
                      Type <SortIcon column="configuration" />
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold tracking-wide uppercase text-primary-900">Status</th>
                    <th className="px-4 py-3 text-xs font-semibold tracking-wide uppercase text-primary-900">Pitch</th>
                    <th className="px-4 py-3 text-xs font-semibold tracking-wide uppercase text-center text-primary-900">Action</th>
                  </tr>
                </thead>
        }
      >
            {loading ? (
              <SkeletonTableRows rows={8} columns={10} />
            ) : sortedPitches.length > 0 ? (
              sortedPitches.map((pitch) => (
                <tr key={pitch.id} className={dataTableRowClass}>
                  <td className={dataTableTdClass}>
                    <button
                      onClick={() => handleStarToggle(pitch.id)}
                      className="transition hover:scale-110"
                    >
                      {pitch.starred ? (
                        <StarSolid className="w-5 h-5 text-warning-500" />
                      ) : (
                        <StarOutline className="w-5 h-5 text-neutral-400 hover:text-warning-500" />
                      )}
                    </button>
                  </td>
                  <td className={dataTableTdClass}>{pitch.companyName || "N/A"}</td>
                  <td className={dataTableTdClass}>{pitch.artistName || "N/A"}</td>
                  <td className={dataTableTdClass}>{pitch.releaseTitle || "N/A"}</td>
                  <td className={dataTableTdClass}>{pitch.releaseDate ? new Date(pitch.releaseDate).toLocaleDateString() : "N/A"}</td>
                  <td className={dataTableTdClass}>{getGenreDisplay(pitch.mainGenre)}</td>
                  <td className={dataTableTdClass}>{pitch.configuration || "N/A"}</td>
                  <td className={dataTableTdClass}>
                    {pitch.status ? <StatusBadge status={pitch.status} /> : "N/A"}
                  </td>
                  <td className={cn(dataTableTdClass, "max-w-xs truncate")}>{pitch.shortDescription || "N/A"}</td>
                  <td className={cn(dataTableTdClass, "text-center")}>
                    <Menu as="div" className="relative inline-block text-left">
                          <Menu.Button className={rowMenuTriggerClass}>
                            <EllipsisVerticalIcon className="w-5 h-5" />
                          </Menu.Button>
                          <Menu.Items
                            modal={false}
                            transition
                            anchor={{ to: "bottom end", gap: 4 }}
                            className={cn(rowMenuPanelClass, rowMenuTransitionClass)}
                          >
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => handleViewPitch(pitch)}
                                    className={`${active ? "bg-primary-50" : ""} group flex w-full items-center px-4 py-2 text-sm transition-colors text-neutral-700`}
                                  >
                                    <EyeIcon className="w-4 h-4 mr-2 text-neutral-500" />
                                    View
                                  </button>
                                )}
                              </Menu.Item>
                          </Menu.Items>
                    </Menu>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-sm text-center text-neutral-500">
                  {hasActiveFilters ? "No pitches found matching your criteria." : "No pitches available."}
                </td>
              </tr>
            )}
      </DataTable>

      {/* Pagination */}
      <div className="mt-6">
        <Pagination
          page={currentPage}
          totalPages={totalPages}
          setPage={handlePageChange}
          totalItems={totalItems}
          limit={itemsPerPage}
        />
      </div>

      {/* View Pitch Modal */}
      {isViewModalOpen && selectedPitch && (
        <ModalRenderer
          type="VIEW_PITCH_DSP"
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedPitch(null);
          }}
          pitch={selectedPitch}
        />
      )}
    </div>
  );
};

export default DspDashboard;
